import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "react-query";
import CalendarEvent from "./CalendarEvent";
import { Box, Link, SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import {
  LiveOnlineCourseModel,
  ListRequestOfCatalogFilter,
  CatalogFilter,
} from "services/organizationPortalClient";
import useGlobalContext from "hooks/globalData";
import {
  SchedulerRef,
  RemoteQuery,
  ProcessedEvent,
  EventRendererProps,
} from "@knowfully/ui.common/components/Scheduler/types";
import { Scheduler } from "@knowfully/ui.common";
import { eventTimeFormat } from "utils/dateUtils";
import { format } from "date-fns";
import moment from "moment-timezone";
import { Link as RouterLink } from "react-router-dom";
import contextStore from "stores/contextStore";
import { useIntl } from "react-intl";

const MIN_HEIGHT = 630;

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  wrapper: {
    /* this +/-90px hack is to prevent scheduler's sticky header to overlap OrgPortal's fixed top-bar */
    transform: "translateY(90px)",
    mt: "-90px",
    mb: "90px",
    pb: 1,
    "& .MuiButtonBase-root": {
      "h6, p": {
        color: theme.palette.primary.main,
      },
    },
    ".rs__event__item .MuiButtonBase-root h6": {
      lineHeight: "1.1",
    },
  },
  eventCard: {
    display: "block",
    color: theme.palette.grey[600],
    backgroundColor: theme.palette.grey[200],
    p: 1,
    borderRadius: 1,
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.dark}`,
      outlineOffset: -2,
    },
  },
  eventCardTitle: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    mb: 1,
  },
});

export type CalendarViewProps = {
  filter: CatalogFilter;
  viewSwitch?: React.ReactNode;
};

const CalendarView = ({ filter, viewSwitch }: CalendarViewProps) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const { formatMessage: t } = useIntl();
  const schedulerRef = useRef<SchedulerRef>(null);
  const { catalogService, courseDetailsUrl } = useGlobalContext();
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { organization } = contextStore.useState();

  const fetchEvents = useCallback(
    async (schedulerQuery: RemoteQuery): Promise<ProcessedEvent[]> => {
      const page = 0;
      const rowsPerPage = 1000;

      const result = await queryClient.fetchQuery(
        [
          "getLiveOnlineCourses",
          organization?.id,
          filter,
          page,
          rowsPerPage,
          schedulerQuery,
        ],
        () =>
          catalogService.getLiveOnlineCourses(
            ListRequestOfCatalogFilter.fromJS({
              pageNumber: page,
              pageSize: rowsPerPage,
              parameters: {
                ...filter,
                dayOfWeek: undefined,
                eventStartDate:
                  schedulerQuery.start > new Date()
                    ? schedulerQuery.start
                    : new Date(),
                eventEndDate: schedulerQuery.end,
              },
            })
          )
      );

      if ((result.data?.length ?? 0) === 0) {
        // Hack, because the scheduler does not clear the calendar if empty result returned
        return [{ start: new Date(0), end: new Date(0) } as ProcessedEvent];
      }

      return (result.data ?? []).map(
        (sku) =>
          ({
            ...sku,
            event_id: sku.id,
            start: sku.eventStartDate,
            end: sku.eventEndDate,
            color: theme.palette.grey[200],
          }) as ProcessedEvent
      );
    },
    [queryClient, organization?.id, filter, catalogService, theme.palette.grey]
  );

  useEffect(() => {
    schedulerRef.current?.scheduler.handleState(fetchEvents, "getRemoteEvents");
  }, [fetchEvents]);

  useEffect(() => {
    const view = isMobile ? "dayAgenda" : "month";
    schedulerRef.current?.scheduler.handleState(view, "view");
    schedulerRef.current?.scheduler.handleState(
      isMobile,
      "disableViewNavigator"
    );
  }, [isMobile]);

  const DayAgendaEventCard = ({ event }: EventRendererProps) => {
    const startTime =
      event.eventStartDate && format(event.eventStartDate, eventTimeFormat);
    const endTime =
      event.eventEndDate && format(event.eventEndDate, eventTimeFormat);
    const timeZone = moment.tz(moment.tz.guess()).zoneAbbr();
    return (
      <Link
        component={RouterLink}
        to={courseDetailsUrl(event.id)}
        underline="none"
        sx={styles.eventCard}
      >
        <Box sx={styles.eventCardTitle}>{event.title}</Box>
        <Box>{t({ id: "creditHoursShort" })}: {event.credits}</Box>
        <Box>{`${startTime} - ${endTime} (${timeZone})`}</Box>
      </Link>
    );
  };

  return (
    <Box sx={styles.wrapper}>
      <Box>{viewSwitch}</Box>
      <Scheduler
        ref={schedulerRef}
        view="month"
        height={MIN_HEIGHT}
        month={{
          weekDays: [0, 1, 2, 3, 4, 5, 6],
          weekStartOn: 0,
          startHour: 0,
          endHour: 24,
        }}
        week={{
          weekDays: [0, 1, 2, 3, 4, 5, 6],
          weekStartOn: 0,
          startHour: 0,
          endHour: 24,
          step: 60,
        }}
        dayAgenda={{}}
        eventRendererDayAgenda={DayAgendaEventCard}
        getRemoteEvents={fetchEvents}
        deletable={false}
        editable={false}
        customViewer={(event, close) => (
          <CalendarEvent
            event={event as any as LiveOnlineCourseModel}
            onClose={close}
          />
        )}
      />
    </Box>
  );
};

export default CalendarView;
